// Shared pieces of the settings pages: the segmented choice, a labelled form row that a search
// can hide, and the capture fields a handling profile knows.

import { useEffect, useRef, type ReactNode } from "react";
import { t } from "../i18n.js";

/** The bar at the top of a detail page: back to the list, the thing's name. */
export function DetailBar({ title, sub, onBack, right }: { title: ReactNode; sub?: ReactNode; onBack: () => void; right?: ReactNode }) {
  return (
    <div className="detail-bar">
      <button type="button" className="abtn" onClick={onBack}>← {t("common.back")}</button>
      <h3>{title}</h3>
      {sub && <span className="mut">{sub}</span>}
      {right && <span style={{ marginLeft: "auto" }}>{right}</span>}
    </div>
  );
}

/** A detail page gets a history entry, so the browser's back button closes it (owner 2026-11-01:
 *  a detail page one can back out of, instead of a narrow side column). */
export function useDetailHistory(open: boolean, onBack: () => void): void {
  const was = useRef(false);
  const cb = useRef(onBack);
  cb.current = onBack;
  useEffect(() => {
    if (open && !was.current) { history.pushState({ trailDetail: true }, ""); was.current = true; }
    else if (!open && was.current) { was.current = false; if (history.state?.trailDetail) history.back(); }
  }, [open]);
  useEffect(() => {
    const onPop = () => { if (was.current) { was.current = false; cb.current(); } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}

export function Seg<T extends string>({ value, options, onChange }: { value: T; options: { v: T; l: string }[]; onChange: (v: T) => void }) {
  return <span className="seg">{options.map((o) => <button key={o.v} type="button" className={value === o.v ? "on" : ""} onClick={() => onChange(o.v)}>{o.l}</button>)}</span>;
}

export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return <><span>{label}</span><div>{children}</div></>;
}

/** One settings row: label, control, optional hint. With a search active it renders only when
 *  the label or hint matches (owner 2026-10-30: the page is long and will grow). */
export function Row({ label, hint, search, children }: { label: string; hint?: string | undefined; search?: string | undefined; children: ReactNode }) {
  const q = (search ?? "").trim().toLowerCase();
  if (q && !label.toLowerCase().includes(q) && !(hint ?? "").toLowerCase().includes(q)) return null;
  return (
    <>
      <span>{label}</span>
      <div>
        {children}
        {hint && <div className="hint">{hint}</div>}
      </div>
    </>
  );
}

export type Req = "required" | "optional" | "forbidden";
export const CAPTURE_FIELDS: { key: "lot" | "bestBefore" | "expiry" | "productionDate" | "serial" | "netContent" | "temperature" | "photo"; label: string; ai: string }[] = [
  { key: "lot", label: "Batchnummer", ai: "(10)" },
  { key: "bestBefore", label: "Bäst före", ai: "(15)" },
  { key: "expiry", label: "Utgångsdatum", ai: "(17)" },
  { key: "productionDate", label: "Produktionsdatum", ai: "(11)" },
  { key: "serial", label: "Serienummer", ai: "(21)" },
  { key: "netContent", label: "Innehåll", ai: "(30/310n/311n)" },
  { key: "temperature", label: "Temperatur", ai: "" },
  { key: "photo", label: "Foto", ai: "" },
];
export const REQ_OPTIONS: { v: Req; l: string }[] = [{ v: "required", l: "Krävs" }, { v: "optional", l: "Valfritt" }, { v: "forbidden", l: "Önskas ej" }];
